/**
 * Forked from packages/framework/Bootstrap/Bootstrap.tsx so CFR's alert/2FA paths go through
 * @creditflowresearch/uilib/Alert instead of @dealroadshow/uikit-alert (see TwoFactorAuthentication/
 * and GlobalErrorContext.tsx forks in this folder). App Router migration (/app-router-migration,
 * apps/allocate/app/layout.tsx reference): when decomposing this into BootstrapBanners/
 * BootstrapClientGlobalListeners/ClientAlertContainer/FeatureToggle, keep wiring the CFR-local
 * GlobalErrorContext/TwoFactorAuthentication forks and uilib's InitAlerts — do not fall back to
 * the shared framework/uikit-alert imports the allocate reference uses.
 */
import React, { useEffect } from 'react';
import fetchIntercept from 'fetch-intercept';
import TwoFactorAuthentication from '@/creditflowresearch/infrastructure/bootstrap/TwoFactorAuthentication/TwoFactorAuthentication';
import SentryUserDataWrapper from '@framework/bootstrap/SentryUserDataWrapper';
import { GoogleAnalyticsProvider } from '@framework/browser/analytics/google';
import GlobalErrorContext from '@/creditflowresearch/infrastructure/bootstrap/GlobalErrorContext';

import SessionContext from '@users/session/SessionContext';
import { InitAlerts } from '@creditflowresearch/uilib/Alert';
import registerMessagesExecCallback from '@framework/message/registerMessagesExecCallback';
import DIContext from '@framework/di/DIContext';
import Container from '@framework/di/Container';
import { TYPES } from '@framework/config/DI/types';
import { checkSession } from '@users/session/interceptors/fetch/checkSession';
import { InitSessionState } from '@users/session/serverSide/InitSessionState';
import { ISession } from '@users/session/vo/Session';
import { IUser } from '@users/user/vo/User';
import ClientCookie from '@framework/router/Next/cookie/ClientCookie';
import ClientReqData from '@framework/router/Next/headers/ClientReqData';

interface IProps {
  container: Container,
  session: {
    initialized: boolean,
    currentSession: ISession,
    currentUser: IUser,
    currentHost: string,
    fingerprint: string,
    userAgent: string,
  },
  children: React.ReactNode,
  cookies: { [key: string]: any },
}

function Bootstrap({ session, children, container, cookies }: IProps) {
  container.bindContextValue(ClientCookie, new ClientCookie(cookies), true);
  container.bindContextValue(ClientReqData, new ClientReqData({
    userAgent: session?.userAgent,
    fingerprint: session?.fingerprint,
    currentHost: session?.currentHost,
  }), true);
  container.bindContextValue(InitSessionState, session, true);

  const nonce = container.get<string>(TYPES.Nonce) || undefined;

  useEffect(() => {
    // @ts-ignore
    fetchIntercept.register({ response: checkSession(container) });
    registerMessagesExecCallback(container);
  }, []);

  return (
    <DIContext container={ container }>
      <InitAlerts />
      <SessionContext>
        <SentryUserDataWrapper>
          <GoogleAnalyticsProvider nonce={ nonce } />
          <GlobalErrorContext>
            <TwoFactorAuthentication>
              { children }
            </TwoFactorAuthentication>
          </GlobalErrorContext>
        </SentryUserDataWrapper>
      </SessionContext>
    </DIContext>
  );
}

export default Bootstrap;
